/**
 * `<ProDocExtraction>` — what the engine read out of a document, **beside the
 * page it read it from** (engine W15, FR-E13 – FR-E16, FR-E41, plan D32).
 *
 * - **The fields**, laid out from the profile's own payload: the top level's,
 *   each nested section's, and each list of records (an invoice's lines, a
 *   statement's transactions, Form 16's quarters, a GRN's quantities — engine
 *   W17) as a table. Money is integer paise and is shown in rupees from its
 *   digits (FR-E16); a quantity is the text printed, set right like a figure.
 * - **The evidence for each field** (FR-E15): a box drawn on the page where a
 *   text layer, a table or OCR read it; a cell of a spreadsheet; a JSON file's
 *   pointer or a text export's line (engine W18, C-E13 — a file read as
 *   structure has no page, so it is said like a cell); the inputs of a
 *   calculated field; or why it is empty. Picking a field shows its page and
 *   draws its box; picking a row draws every field of it.
 * - **The checks** (FR-E13): each with its result, whether it gates, and — in
 *   paise — what was expected, what was found and by how much they differ;
 *   after corrections, the kernel's recheck beside the original (FR-E21).
 * - **How it was read**: the channel, the rungs that ran and which were
 *   adopted (FR-E27), the two confidence axes, never merged (FR-E28).
 *
 * Reading the fields **is** reading the document: the service writes a read
 * row, purpose `extraction`, before it answers (FR-E17). Its fields are served
 * once the review allows (FR-E19) — to a reviewer always, to anyone else once
 * accepted — so a reader who asks early is told it waits, and why.
 *
 * `<ProDocExtractionReview>` (`extraction-review.tsx`) is this screen with the
 * corrections and the decision.
 *
 * W23 (FR-51, FR-52): **a field that holds personal data comes masked** — an
 * employee's PAN, a statement's account number — named by the read's
 * `masked_paths`. It is shown masked, said to be, and offered *Reveal* to a
 * person holding `personal_data:can_view`: one field's value, logged by the
 * service first, kept in this view's state only and forgotten when the person
 * leaves it or the extraction changes. A masked field's correction is typed in
 * whole — a masked form is never sent back as a value.
 *
 * W24 (FR-56, D72, D73, C-E16): **each field carries a chip from its checks** —
 * *Holds*, *Broken*, *Not checked*, and *Edited* once corrected until the kernel
 * re-checked it — on its row and in its table cell, never a model's confidence
 * (engine FR-E28). A check names the fields it read (kernel 0.20.0); one read
 * before that names none, so its fields are all *not checked*. **Show read
 * areas** draws every field's box on the page at once, remembered per browser.
 */
import { Alert, Badge, Button, Card, CardBody, CardHeader, DataTable, DescriptionList, Detail, EmptyState, Input, Select, Skeleton, SkeletonRegion, SkeletonText, Switch, TONE_TEXT, badgeClass, cn, formatDateTime, type BadgeTone, type ColumnDef } from '@fiveox/ui'
import { CircleCheck, CircleDashed, CircleX, LocateFixed, Pencil, Undo2, type LucideIcon } from 'lucide-react'
import { useCallback, useMemo, useState, type ReactNode } from 'react'

import { isMaskedPath } from './personal-data'
import { RevealButton, useMayReveal, useReveals, type Reveals } from './personal-data-panel'

import { EvidencePage, type ImageOpener, type PageMark, type PdfOpener } from './evidence-page'
import {
  channelLabel,
  readerLabel,
  checkLabel,
  checksNameFields,
  chipFor,
  emptyKindOf,
  evidenceFor,
  evidenceText,
  extractionStateLabel,
  fieldText,
  formatPaise,
  identityBandLabel,
  isQuantity,
  latestRecheck,
  layoutFields,
  orderChecks,
  paiseToRupees,
  reviewReasonLabel,
  rungLabel,
  rungText,
  type Check,
  type ChipState,
  type Evidence,
  type Extraction,
  type ExtractionRecord,
  type FieldChip,
  type FieldEntry,
  type FieldKind,
  type FieldTable,
} from './extraction-model'
import { useLoad, useProDocClient, type HostProps } from './hooks'
import { scanOf } from './page-geometry'
import { Preview } from './preview'
import { appName, documentTypeLabel, fileKindOf, notReadyOf, passwordSavedOf, type DocumentTypeOption, type DocumentVersion, type ProDocClient, type ProDocDocument } from './prodoc-client'

/* ------------------------------------------------------------- marks */

const STATE_TONE: Record<string, BadgeTone> = {
  pending: 'neutral',
  app_review: 'info',
  needs_review: 'warning',
  accepted: 'success',
  rejected: 'error',
}

/** An extraction's review state as a badge (FR-E19). */
export function ExtractionStateBadge({ state, size = 'md' }: { state: string | null | undefined; size?: 'sm' | 'md' }) {
  return (
    <Badge tone={STATE_TONE[state ?? ''] ?? 'neutral'} size={size}>
      {extractionStateLabel(state)}
    </Badge>
  )
}

/** Whether every gating check holds and something was read (FR-E14). */
export function ReconciledBadge({ reconciled, size = 'md' }: { reconciled: boolean; size?: 'sm' | 'md' }) {
  return (
    <Badge tone={reconciled ? 'success' : 'error'} size={size}>
      {reconciled ? 'Adds up' : 'Does not add up'}
    </Badge>
  )
}

const RESULT: Record<string, { label: string; tone: BadgeTone }> = {
  verified: { label: 'Holds', tone: 'success' },
  broken: { label: 'Broken', tone: 'error' },
  not_applicable: { label: 'Not checked', tone: 'neutral' },
}

function ResultBadge({ result }: { result: string }) {
  const def = RESULT[result] ?? { label: result, tone: 'neutral' as BadgeTone }
  return (
    <Badge tone={def.tone} size="sm">
      {def.label}
    </Badge>
  )
}

/** A field's chip (W24, FR-56): the check results' own words, and *Edited* while a correction waits for its recheck. */
const CHIP: Record<ChipState, { label: string; tone: BadgeTone; icon: LucideIcon }> = {
  broken: { label: 'Broken', tone: 'error', icon: CircleX },
  holds: { label: 'Holds', tone: 'success', icon: CircleCheck },
  not_checked: { label: 'Not checked', tone: 'neutral', icon: CircleDashed },
  edited: { label: 'Edited', tone: 'info', icon: Pencil },
}

const namesOf = (names: string[]): string => names.map(checkLabel).join(', ')
const holdText = (names: string[]): string => `${namesOf(names)} ${names.length === 1 ? 'does' : 'do'} not hold`

/** What a chip means, in a sentence: which checks decided it — `legacy` for an extraction whose checks name no fields (read before W24). */
export function chipText(chip: FieldChip, legacy = false): string {
  let text: string
  switch (chip.state) {
    case 'edited':
      text = 'Edited — the checks are being run again on the corrections.'
      break
    case 'broken':
      text = `Broken — ${holdText(chip.checks)}.`
      break
    case 'holds':
      text = `Holds — checked by ${namesOf(chip.checks)}.`
      break
    default:
      text = legacy ? 'Not checked — this was read before checks said which fields they read.' : 'Not checked — no check reads this field.'
  }
  if (chip.edited && chip.state !== 'edited') text = `Edited, and checked again: ${text.charAt(0).toLowerCase()}${text.slice(1)}`
  if (chip.advisory.length) text += ` Advisory: ${holdText(chip.advisory)}.`
  return text
}

/** The chip itself: a pill on a field's row; on a table's cell (`compact`), its mark alone, named for a screen reader. */
function CheckChip({ chip, legacy, compact = false }: { chip: FieldChip; legacy: boolean; compact?: boolean }) {
  const def = CHIP[chip.state]
  const text = chipText(chip, legacy)
  const marks = { 'data-chip': chip.state, ...(chip.edited ? { 'data-edited': '' } : {}) }
  if (compact) {
    const Icon = def.icon
    return (
      <span role="img" aria-label={text} title={text} className={cn('inline-flex shrink-0', def.tone === 'neutral' ? 'text-muted-foreground' : TONE_TEXT[def.tone])} {...marks}>
        <Icon size={13} aria-hidden />
      </span>
    )
  }
  return (
    <span className={badgeClass({ tone: def.tone, size: 'sm' })} title={text} {...marks}>
      {chip.edited && chip.state !== 'edited' && (
        <>
          <Pencil size={10} aria-hidden />
          <span className="sr-only">Edited, </span>
        </>
      )}
      {def.label}
    </span>
  )
}

/** Where *Show read areas* is remembered, in this browser only (D73). */
export const READ_AREAS_KEY = 'prodoc.extraction.read-areas'

/** Whether this browser asked for the read areas — off where storage is blocked. */
function readAreasRemembered(): boolean {
  try {
    return localStorage.getItem(READ_AREAS_KEY) === 'on'
  } catch {
    return false
  }
}

function rememberReadAreas(on: boolean): void {
  try {
    localStorage.setItem(READ_AREAS_KEY, on ? 'on' : 'off')
  } catch {
    // A browser that keeps nothing: on for this view only.
  }
}

/** "What was read as" — the profile the engine found for an `auto` request, else the one asked for. */
export const profileOf = (record: Pick<ExtractionRecord, 'profile' | 'profile_found'>): string => record.profile_found ?? record.profile

/* -------------------------------------------------------- the loader */

export interface LoadedExtraction {
  doc: ProDocDocument | null
  types: DocumentTypeOption[]
  extraction: Extraction | null
  /** The record alone, when the fields wait for the review (FR-E19). */
  waiting: { record: ExtractionRecord | null; state: string | null; reasons: string[] } | null
  loading: boolean
  error: string
  reload: () => Promise<void>
}

/** The document, its type names, and the extraction — or, while its review keeps the fields back, its record and why. */
export function useExtraction(client: ProDocClient, documentId: string, extractionId: string, revision = 0): LoadedExtraction {
  const doc = useLoad(() => client.documents.get(documentId), [client, documentId])
  const options = useLoad(() => client.documents.options(), [client])
  const loaded = useLoad(async () => {
    try {
      return { extraction: await client.parsing.extraction(documentId, extractionId), waiting: null }
    } catch (e) {
      const notReady = notReadyOf(e)
      if (!notReady) throw e
      // The record carries no content and needs no review: the list says what it is.
      const records = await client.parsing.extractions(documentId).catch(() => [])
      return { extraction: null, waiting: { record: records.find((r) => r.id === extractionId) ?? null, ...notReady } }
    }
  }, [client, documentId, extractionId, revision])
  return {
    doc: doc.data,
    types: options.data?.document_types ?? [],
    extraction: loaded.data?.extraction ?? null,
    waiting: loaded.data?.waiting ?? null,
    loading: doc.loading || loaded.loading,
    error: doc.error || loaded.error,
    reload: async () => {
      await Promise.all([doc.reload(), loaded.reload()])
    },
  }
}

/* ------------------------------------------------------ the editor */

/** The review's half of a field row: what a person has typed and not yet saved, by field path. */
export interface FieldEditor {
  /** Typed text by path — `''` clears the field. */
  draft: Record<string, string>
  errors: Record<string, string>
  edit: (path: string, text: string, kind: FieldKind) => void
  undo: (path: string) => void
}

/** A value as a person types it back: rupees for paise, the ISO date, the text, yes or no. */
export function editableText(value: unknown, kind: FieldKind): string {
  if (value === null || value === undefined) return ''
  if (kind === 'money' && typeof value === 'number') return paiseToRupees(value)
  if (typeof value === 'boolean') return value ? 'yes' : 'no'
  return String(value)
}

const EDITABLE: FieldKind[] = ['money', 'date', 'text', 'boolean', 'empty']

/* -------------------------------------------------------- the view */

type Selection = { path: string; row: string | null } | null

export interface ExtractionViewProps {
  client: ProDocClient
  doc: ProDocDocument
  extraction: Extraction
  types: DocumentTypeOption[]
  openPdf?: PdfOpener
  openImage?: ImageOpener
  /** The review's corrections, when the person may make them. */
  editor?: FieldEditor | null
  /** Above everything in the side column — the review's decision. */
  aside?: ReactNode
  /** Under the checks' heading — the recheck's state, in the review. */
  checksNote?: ReactNode
  /** The failing checks above the fields (the review), or the fields first (a reader). */
  checksFirst?: boolean
}

/**
 * The page beside what was read from it. Shared by `<ProDocExtraction>` and
 * `<ProDocExtractionReview>`, which adds the editor and the decision.
 */
export function ExtractionView({ client, doc, extraction, types, openPdf, openImage, editor = null, aside, checksNote, checksFirst = false }: ExtractionViewProps) {
  const [selected, setSelected] = useState<Selection>(null)
  // The accepted view — the fields with every correction applied — over what was read (FR-E21).
  const fields = extraction.view ?? extraction.fields
  const layout = useMemo(() => layoutFields(fields), [fields])
  const corrected = useMemo(() => {
    const out = new Map<string, unknown>()
    // The value as first read, for every path a correction touched.
    for (const c of [...extraction.corrections].sort((a, b) => a.revision - b.revision)) if (!out.has(c.field_path)) out.set(c.field_path, c.from)
    return out
  }, [extraction.corrections])

  // D73: *Show read areas* — every field's box on the page at once, remembered per browser.
  const [readAreas, setReadAreas] = useState(readAreasRemembered)
  const hasBoxes = useMemo(() => Object.values(extraction.evidence).some((ev) => ev?.box && ev.page !== null), [extraction.evidence])
  const picked = useMemo(() => marksFor(extraction.evidence, layout.tables, selected), [extraction.evidence, layout.tables, selected])
  const marks = useMemo(() => (readAreas ? marksFor(extraction.evidence, layout.tables, selected, true) : picked), [readAreas, picked, extraction.evidence, layout.tables, selected])
  // The page follows what is picked, never the read areas — turning them on leaves the person on their page.
  const focusPage = picked.find((m) => m.selected)?.page ?? picked[0]?.page ?? null

  // W24 (FR-56, D72): each field's chip from its checks — the latest revision's recheck once it ran, else the originals.
  const recheck = useMemo(() => latestRecheck(extraction), [extraction])
  const chipChecks = recheck ?? extraction.checks
  const legacy = chipChecks.length > 0 && !checksNameFields(chipChecks)
  const chipOf = useCallback((path: string) => chipFor(path, extraction.checks, recheck, extraction.corrections), [extraction.checks, recheck, extraction.corrections])
  // How each page of a scan was read — the page is turned by it before its boxes are drawn (T15.18).
  const scan = useMemo(() => scanOf(extraction.rungs), [extraction.rungs])
  // The file the extraction read decides how its pages are drawn (T16.20): the current version's is the document's
  // own; an earlier version's — which may be another kind of file — is looked up in the document's versions.
  const readCurrent = extraction.version_id === doc.current_version_id
  // W30 (FR-67): an encrypted current version whose record does not say its password is saved — its version may.
  const askSaved = readCurrent && doc.encrypted && !passwordSavedOf(doc)
  const versions = useLoad<DocumentVersion[] | null>(
    () => (readCurrent && !askSaved ? Promise.resolve(null) : client.documents.versions.list(doc.id)),
    [client, doc.id, readCurrent, askSaved],
  )
  const versionRead = readCurrent
    ? {
        media_type: doc.media_type,
        encrypted: doc.encrypted,
        password_saved: passwordSavedOf(doc) || versions.data?.find((v) => v.id === doc.current_version_id)?.password_saved === true,
      }
    : (versions.data?.find((v) => v.id === extraction.version_id) ?? null)
  const lookingUp = !readCurrent && !versionRead && versions.loading
  const lookupFailed = !readCurrent && !versionRead && !versions.loading && versions.error !== ''
  // A PDF is drawn with its boxes; a scanned image too, once the extraction says its pages' sizes (T15.17).
  const readKind = versionRead?.media_type ?? null
  const drawsPage = readKind === 'application/pdf' || (fileKindOf(readKind) === 'image' && extraction.pages.length > 0)

  // W23 (FR-51, FR-52): the fields the service masked for this person, and their reveals — this view's state only,
  // forgotten when the extraction or its revision changes, and with the view when the person leaves.
  const masked = extraction.masked_paths ?? []
  const mayReveal = useMayReveal(client, masked.length > 0)
  const reveals = useReveals(`${extraction.id}:${extraction.review?.revision ?? 0}:${extraction.payload_sha256 ?? ''}`)
  const onReveal = (path: string) => void reveals.reveal(path, async () => (await client.parsing.reveal(doc.id, extraction.id, path)).value)

  const rowProps: RowProps = { evidence: extraction.evidence, corrected, editor, selected, onSelect: setSelected, masked, reveals, mayReveal, onReveal, chipOf, legacy }

  const checks = (
    <ChecksCard checks={extraction.checks} rechecks={extraction.view_checks ?? extraction.review?.rechecks ?? null} ordered={checksFirst} note={checksNote} />
  )

  return (
    <div className="@container">
      <div className="grid items-start gap-6 @3xl:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
        <Card padding="md" className="@3xl:sticky @3xl:top-20 @3xl:max-h-[calc(100vh-6rem)] @3xl:overflow-auto">
          {doc.status === 'expired' ? (
            <EmptyState variant="outline" title="The file is gone" description="Its bytes were removed from the store. What was read from it stays on file." />
          ) : lookingUp ? (
            <Skeleton className="h-[60vh] w-full rounded-lg" />
          ) : lookupFailed ? (
            <Alert tone="error">
              The version this was read from could not be looked up: {versions.error}{' '}
              <Button variant="link" onClick={() => void versions.reload()}>
                Try again
              </Button>
            </Alert>
          ) : drawsPage ? (
            <>
              {hasBoxes && (
                <div className="mb-2 flex justify-end">
                  <Switch
                    label="Show read areas"
                    checked={readAreas}
                    onChange={(e) => {
                      setReadAreas(e.target.checked)
                      rememberReadAreas(e.target.checked)
                    }}
                  />
                </div>
              )}
              <EvidencePage
                client={client}
                doc={doc}
                versionId={extraction.version_id}
                version={versionRead}
                marks={marks}
                page={focusPage}
                pages={extraction.pages}
                scan={scan}
                openPdf={openPdf}
                openImage={openImage}
              />
            </>
          ) : (
            <div className="space-y-3">
              <Preview client={client} doc={doc} />
              <p className="text-muted-foreground text-xs">
                {extraction.stale ? 'This is the current file; the extraction read an earlier version. ' : ''}
                Where each field was read — its cell, line, JSON pointer, page or calculation — is listed beside it.
              </p>
            </div>
          )}
        </Card>

        <div className="space-y-6">
          {aside}
          {checksFirst && checks}
          <Card>
            <CardHeader
              title="What was read"
              description={editor ? 'Correct a field where the page says otherwise; the checks are run again on your corrections.' : 'Pick a field to see where on the page it was read.'}
            />
            <CardBody className="space-y-6">
              {layout.sections.length === 0 && layout.tables.length === 0 && <p className="text-muted-foreground text-sm">Nothing was read.</p>}
              {layout.sections.map((section) => (
                <section key={section.path ?? 'top'} aria-label={section.title}>
                  {section.path !== null && <h3 className="text-foreground mb-1 text-sm font-semibold">{section.title}</h3>}
                  <div className="divide-border divide-y">
                    {section.entries.map((entry) => (
                      <FieldRow key={entry.path} entry={entry} {...rowProps} />
                    ))}
                  </div>
                </section>
              ))}
              {layout.tables.map((table) => (
                <FieldTableView key={table.path} table={table} {...rowProps} />
              ))}
            </CardBody>
          </Card>
          {!checksFirst && checks}
          <HowItWasRead extraction={extraction} types={types} />
        </div>
      </div>
    </div>
  )
}

/**
 * The boxes to draw for what is picked: the field's own, strongest, and the rest of its row lighter; or a whole row's.
 * With `readAreas` (D73), **every box the evidence holds** besides, lighter — the evidence page draws those of the
 * page it shows, so every field's box on the current page is drawn at once.
 */
function marksFor(evidence: Record<string, Evidence | null>, tables: FieldTable[], selected: Selection, readAreas = false): PageMark[] {
  const marks: PageMark[] = []
  const drawn = new Set<string>()
  // A field without a box of its own falls back to its row's only when it is the one picked — a row's box is drawn once.
  const add = (path: string, isSelected: boolean, label: string, own = false) => {
    const ev = own ? (evidence[path] ?? null) : evidenceFor(evidence, path)
    if (!ev?.box || ev.page === null || drawn.has(path)) return
    drawn.add(path)
    marks.push({ key: path, page: ev.page, box: ev.box, selected: isSelected, label })
  }
  if (selected?.row) {
    const row = tables.flatMap((t) => t.rows).find((r) => r.path === selected.row)
    const ownBox = selected.path !== selected.row && Boolean(evidence[selected.path]?.box)
    if (ownBox) add(selected.path, true, selected.path, true)
    add(selected.row, !ownBox, selected.row, true)
    for (const entry of Object.values(row?.entries ?? {})) add(entry.path, false, entry.label, true)
  } else if (selected) {
    add(selected.path, true, selected.path)
  }
  if (readAreas) for (const path of Object.keys(evidence)) add(path, false, path, true)
  return marks
}

interface RowProps {
  evidence: Record<string, Evidence | null>
  corrected: Map<string, unknown>
  editor: FieldEditor | null
  selected: Selection
  onSelect: (s: Selection) => void
  /** W23: the paths the service masked for this person (`masked_paths`). */
  masked: readonly string[]
  /** W23: what this view revealed, by path — its state only. */
  reveals: Reveals
  /** W23: `personal_data:can_view`. */
  mayReveal: boolean
  onReveal: (path: string) => void
  /** W24 (FR-56): a field's chip from its checks, by path. */
  chipOf: (path: string) => FieldChip
  /** W24: the checks name no fields — an extraction read before kernel 0.20.0. */
  legacy: boolean
}

/** One field: its label, its value (corrected or as read), where it was read, its chip — and, in the review, its correction. */
function FieldRow({ entry, evidence, corrected, editor, selected, onSelect, masked, reveals, mayReveal, onReveal, chipOf, legacy, row = null, kindHint }: RowProps & { entry: FieldEntry; row?: string | null; kindHint?: FieldKind }) {
  const ev = evidenceFor(evidence, entry.path)
  const editKind = entry.kind === 'empty' ? (kindHint && kindHint !== 'empty' ? kindHint : emptyKindOf(entry.key)) : entry.kind
  const shownKind = entry.kind === 'empty' ? editKind : entry.kind
  const editing = editor ? editor.draft[entry.path] : undefined
  const isEditing = editing !== undefined
  const wasCorrected = corrected.has(entry.path)
  const isSelected = selected?.path === entry.path
  const mayCorrect = Boolean(editor) && EDITABLE.includes(entry.kind)
  const inputId = `prodoc-field-${entry.path.replace(/[^A-Za-z0-9_-]/g, '-')}`
  const empty = entry.value === null || entry.value === undefined
  // W23: a masked field — its value as the service masked it, until this person reveals it here.
  const isMasked = !empty && isMaskedPath(masked, entry.path)
  const revealed = isMasked ? reveals.values.get(entry.path) : undefined
  const isRevealed = revealed !== undefined
  const revealError = isMasked ? reveals.errors[entry.path] : undefined

  return (
    <div className={cn('flex items-start gap-3 rounded-lg px-2 py-2.5', isSelected && 'bg-muted')} data-field={entry.path} {...(isMasked ? { 'data-masked': isRevealed ? 'revealed' : 'masked' } : {})}>
      <div className="min-w-0 flex-1">
        {isEditing ? (
          <FieldInput id={inputId} label={entry.label} kind={editKind} value={editing} masked={isMasked} error={editor?.errors[entry.path]} onChange={(text) => editor?.edit(entry.path, text, editKind)} />
        ) : (
          <>
            <p className="text-muted-foreground text-xs">{entry.label}</p>
            <p className={cn('text-sm break-words', empty ? 'text-muted-foreground italic' : 'text-foreground', isMasked && 'font-mono')} {...(isRevealed ? { 'data-revealed': '' } : {})}>
              {empty ? 'Not read' : fieldText(isRevealed ? revealed : entry.value, shownKind)}
            </p>
            {isMasked && (
              <p className="text-muted-foreground mt-0.5 text-xs">
                {isRevealed ? 'Personal data — revealed, and shown only until you leave this page.' : mayReveal ? 'Personal data — masked.' : 'Personal data — masked. Revealing it needs the personal-data permission.'}
              </p>
            )}
            {revealError && <p className={cn('mt-0.5 text-xs', TONE_TEXT.error)}>{revealError}</p>}
          </>
        )}
        {wasCorrected && (
          // A masked field's earlier value is never said here — the correction is, without it.
          <p className={cn('mt-0.5 text-xs', TONE_TEXT.info)}>{isMasked ? 'Corrected' : `Corrected — read as ${fieldText(corrected.get(entry.path), shownKind)}`}</p>
        )}
        <p className="text-muted-foreground mt-0.5 text-xs">{evidenceText(ev, entry.key)}</p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <CheckChip chip={chipOf(entry.path)} legacy={legacy} />
        {isMasked && mayReveal && !isEditing && (
          <RevealButton label={entry.label} revealed={isRevealed} busy={reveals.busy === entry.path} onReveal={() => onReveal(entry.path)} onHide={() => reveals.hide(entry.path)} compact />
        )}
        {ev?.box && ev.page !== null && (
          <Button variant="ghost" size="sm" aria-label={`Show ${entry.label} on the page`} title="Show it on the page" onClick={() => onSelect({ path: entry.path, row })}>
            <LocateFixed size={15} aria-hidden />
          </Button>
        )}
        {mayCorrect &&
          (isEditing ? (
            <Button variant="ghost" size="sm" aria-label={`Undo the correction of ${entry.label}`} title="Undo" onClick={() => editor?.undo(entry.path)}>
              <Undo2 size={15} aria-hidden />
            </Button>
          ) : (
            <Button variant="ghost" size="sm" aria-label={`Correct ${entry.label}`} title="Correct it" onClick={() => editor?.edit(entry.path, editableText(entry.value, editKind), editKind)}>
              <Pencil size={15} aria-hidden />
            </Button>
          ))}
      </div>
    </div>
  )
}

/** A correction's control, by what the field holds: rupees for money, a date, yes or no, or text. */
function FieldInput({ id, label, kind, value, masked = false, error, onChange }: { id: string; label: string; kind: FieldKind; value: string; masked?: boolean; error?: string; onChange: (text: string) => void }) {
  if (kind === 'boolean') {
    return (
      <Select id={id} size="sm" label={label} value={value} error={error ?? null} onChange={(e) => onChange(e.target.value)}>
        <option value="">Not on the document</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </Select>
    )
  }
  return (
    <Input
      id={id}
      size="sm"
      label={label}
      value={value}
      inputMode={kind === 'money' ? 'decimal' : undefined}
      type={kind === 'date' ? 'date' : 'text'}
      hint={
        masked
          ? 'Personal data, shown masked — type the whole value as printed. A masked one is not saved.'
          : kind === 'money'
            ? 'In rupees, as printed — 1,23,456.78. Empty if the document does not show it.'
            : kind === 'date'
              ? 'The date on the document. Empty if it does not show one.'
              : 'As printed. Empty if the document does not show it.'
      }
      error={error ?? null}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

type TableRow = FieldTable['rows'][number]

/** A list of records as a table; a picked row's boxes are drawn and its fields listed under it — corrected there, in the review. */
function FieldTableView({ table, ...rowProps }: RowProps & { table: FieldTable }) {
  const { selected, onSelect, corrected, editor, masked, reveals, mayReveal, chipOf, legacy } = rowProps
  const picked = table.rows.find((r) => r.path === selected?.row) ?? null
  const columns: ColumnDef<TableRow>[] = table.columns.map((col) => ({
    key: col.key,
    label: col.label,
    // A quantity — a GRN's ordered, received, accepted and rejected (engine W17) — is set right like a figure, as printed.
    width: col.kind === 'money' ? 120 : isQuantity(col.key) ? 100 : 140,
    align: col.kind === 'money' || isQuantity(col.key) ? 'right' : 'left',
    render: (row) => {
      const entry = row.entries[col.key]
      if (!entry) return ''
      const touched = corrected.has(entry.path) || (editor?.draft[entry.path] !== undefined)
      const empty = entry.value === null || entry.value === undefined
      // W23: a masked cell stays masked; the picked row's field reveals it, and then it shows here too.
      const isMasked = !empty && isMaskedPath(masked, entry.path)
      const revealed = isMasked ? reveals.values.get(entry.path) : undefined
      // W24 (FR-56): the cell's chip — its mark alone, named for a screen reader, so the cell's text stays its value.
      return (
        <span className="inline-flex items-center gap-1" data-cell={entry.path}>
          <span
            className={cn(touched && 'font-semibold', isMasked && 'font-mono')}
            title={touched ? 'Corrected' : isMasked ? (revealed !== undefined ? 'Personal data, revealed' : mayReveal ? 'Personal data, masked — pick the row to reveal it' : 'Personal data, masked') : undefined}
            {...(isMasked ? { 'data-masked': revealed !== undefined ? 'revealed' : 'masked' } : {})}
          >
            {empty ? '—' : fieldText(revealed !== undefined ? revealed : entry.value, entry.kind === 'empty' ? col.kind : entry.kind)}
          </span>
          <CheckChip chip={chipOf(entry.path)} legacy={legacy} compact />
        </span>
      )
    },
  }))
  return (
    <section aria-label={table.title}>
      <h3 className="text-foreground mb-2 text-sm font-semibold">
        {table.title} <span className="text-muted-foreground font-normal">· {table.rows.length} row{table.rows.length === 1 ? '' : 's'}</span>
      </h3>
      <DataTable
        columns={[{ key: '__n', label: '#', width: 48, render: (row: TableRow) => row.index + 1 }, ...columns]}
        rows={table.rows}
        rowKey={(row) => row.path}
        onRowClick={(row) => onSelect({ path: row.path, row: row.path })}
        maxHeight="40vh"
        empty={<p className="text-muted-foreground p-4 text-sm">No rows were read.</p>}
      />
      <p className="text-muted-foreground mt-1 text-xs">Pick a row to see where it was read{editor ? ' and to correct it' : ''}.</p>
      {picked && (
        <div className="border-border mt-3 rounded-lg border p-2" data-row={picked.path}>
          <p className="text-foreground px-2 pt-1 text-sm font-medium">Row {picked.index + 1}</p>
          <div className="divide-border divide-y">
            {table.columns.map((col) => {
              const entry = picked.entries[col.key]
              return entry ? <FieldRow key={entry.path} entry={entry} row={picked.path} kindHint={col.kind} {...rowProps} /> : null
            })}
          </div>
        </div>
      )}
    </section>
  )
}

/* ------------------------------------------------------------ checks */

/** The figures of a check, in rupees from paise: expected, found, and by how much they differ. */
export function checkFigures(check: Check): string | null {
  const parts: string[] = []
  if (check.expected !== null) parts.push(`expected ${formatPaise(check.expected)}`)
  if (check.found !== null) parts.push(`found ${formatPaise(check.found)}`)
  if (check.delta !== null && check.delta !== 0) parts.push(`off by ${formatPaise(Math.abs(check.delta))}`)
  if (parts.length === 0) return null
  const text = parts.join(' · ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/** Every check the profile defines (FR-E13), and — after corrections — the kernel's recheck beside each (FR-E21). */
export function ChecksCard({ checks, rechecks, ordered, note }: { checks: Check[]; rechecks: Check[] | null; ordered: boolean; note?: ReactNode }) {
  const list = ordered ? orderChecks(checks) : checks
  const again = new Map((rechecks ?? []).map((c) => [c.name, c]))
  const broken = checks.filter((c) => c.result === 'broken').length
  return (
    <Card>
      <CardHeader
        title="Checks"
        description={
          checks.length === 0
            ? 'This profile defines no checks.'
            : broken
              ? `${broken} of ${checks.length} do not hold. A gating check decides whether it adds up; an advisory one only informs.`
              : `All ${checks.length} that could be checked hold.`
        }
      />
      <CardBody className="space-y-3">
        {note}
        <ul className="divide-border divide-y" aria-label="Checks">
          {list.map((check) => {
            const after = again.get(check.name)
            const figures = checkFigures(check)
            return (
              <li key={check.name} className="py-2.5" data-check={check.name} data-result={check.result}>
                <div className="flex flex-wrap items-center gap-2">
                  <ResultBadge result={check.result} />
                  <span className="text-foreground text-sm font-medium">{checkLabel(check.name)}</span>
                  <span className="text-muted-foreground text-xs">{check.gating ? 'Gating' : 'Advisory'}</span>
                </div>
                {figures && <p className="text-muted-foreground mt-1 text-xs">{figures}</p>}
                {check.detail && <p className="text-muted-foreground mt-0.5 text-xs">{check.detail}</p>}
                {after && (
                  <p className="mt-1 flex flex-wrap items-center gap-2 text-xs" data-recheck={after.result}>
                    <span className="text-foreground font-medium">After the corrections</span>
                    <ResultBadge result={after.result} />
                    {checkFigures(after) && <span className="text-muted-foreground">{checkFigures(after)}</span>}
                  </p>
                )}
              </li>
            )
          })}
        </ul>
      </CardBody>
    </Card>
  )
}

/* ------------------------------------------------------ how it was read */

function HowItWasRead({ extraction, types }: { extraction: Extraction; types: DocumentTypeOption[] }) {
  const identity = extraction.confidence.identity
  const aiCalls = typeof extraction.cost.ai_calls === 'number' ? extraction.cost.ai_calls : null
  return (
    <Card>
      <CardHeader title="How it was read" description="The two confidences are never merged: the money's comes from the checks alone, the identity's from who and when the document names." />
      <CardBody className="space-y-4">
        <DescriptionList>
          <Detail label="Read as" value={documentTypeLabel(profileOf(extraction), types)} />
          <Detail label="Channel" value={channelLabel(extraction.channel)} />
          <Detail label="Money confidence" value={extraction.confidence.money !== null ? `${extraction.confidence.money} / 100` : null} emptyLabel="Not given" />
          <Detail
            label="Identity"
            value={identity.band ? `${identityBandLabel(identity.band)}${identity.score !== null ? ` · ${identity.score} / 100` : ''}` : null}
            emptyLabel="Not given"
          />
          {extraction.layout && <Detail label="Layout" value={extraction.layout} />}
          {/* W32 (FR-E47, FR-E48): a structured or mapped reading says so, with the map's version it was read under. */}
          {extraction.reader && extraction.reader !== 'document' && <Detail label="Reader" value={readerLabel(extraction.reader)} />}
          {extraction.column_map && <Detail label="Column map" value={extraction.column_map.version !== null ? `Version ${extraction.column_map.version}` : 'Saved map'} />}
          {extraction.rows_extracted !== null && <Detail label="Rows read" value={String(extraction.rows_extracted)} />}
          {aiCalls !== null && <Detail label="AI calls" value={String(aiCalls)} />}
          <Detail label="Read" value={extraction.created_at ? formatDateTime(extraction.created_at) : null} />
        </DescriptionList>
        {extraction.identity_reasons.length > 0 && (
          <p className="text-muted-foreground text-xs">Identity: {extraction.identity_reasons.map((r) => reviewReasonLabel(r).toLowerCase()).join('; ')}.</p>
        )}
        {extraction.flags.length > 0 && (
          <div className="flex flex-wrap gap-1.5" aria-label="Flags">
            {extraction.flags.map((flag) => (
              <Badge key={flag} tone="warning" size="sm">
                {reviewReasonLabel(flag)}
              </Badge>
            ))}
          </div>
        )}
        {extraction.rungs.length > 0 && (
          <div>
            <p className="text-foreground mb-1 text-sm font-medium">Steps</p>
            <ol className="space-y-1 text-sm" aria-label="Steps">
              {extraction.rungs.map((rung, i) => (
                <li key={`${rung.rung}-${i}`} className="flex flex-wrap gap-x-2" data-rung={rung.rung}>
                  <span className="text-foreground">{rungLabel(rung.rung)}</span>
                  <span className={cn('text-xs leading-5', rung.adopted ? TONE_TEXT.success : 'text-muted-foreground')}>{rungText(rung)}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
        <p className="text-muted-foreground text-xs">
          {[extraction.engine_version && `Engine ${extraction.engine_version}`, extraction.kernel_version && `kernel ${extraction.kernel_version}`, extraction.schema_version !== null && `schema ${extraction.schema_version}`]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </CardBody>
    </Card>
  )
}

/* ------------------------------------------------ the review's state */

/** Where its review stands (FR-E19): why it waits, who decided and what they said, or whose review it is. */
export function ReviewStateAlert({ record }: { record: ExtractionRecord }) {
  const review = record.review
  if (!review) return null
  if (review.state === 'needs_review') {
    return (
      <Alert tone="warning">
        <p className="font-medium">Waiting for a reviewer</p>
        {review.reasons.length > 0 && (
          <ul className="mt-1 list-disc pl-5" aria-label="Why it waits">
            {review.reasons.map((r) => (
              <li key={r}>{reviewReasonLabel(r)}</li>
            ))}
          </ul>
        )}
      </Alert>
    )
  }
  if (review.state === 'app_review') {
    return (
      <Alert tone="info">
        {appName(record.owner_app)} reviews what was read from this document in its own screens, and posts its decision back.
        {review.reasons.length > 0 ? ` It was told: ${review.reasons.map((r) => reviewReasonLabel(r).toLowerCase()).join('; ')}.` : ''}
      </Alert>
    )
  }
  if (review.state === 'accepted' || review.state === 'rejected') {
    const who = review.decided_by_app ? appName(review.decided_by_app) : review.decided_by_user ? 'a reviewer' : 'ProDoc, because it added up'
    return (
      <Alert tone={review.state === 'accepted' ? 'success' : 'error'}>
        {review.state === 'accepted' ? 'Accepted' : 'Rejected'} by {who}
        {review.decided_at ? `, ${formatDateTime(review.decided_at)}` : ''}
        {review.comment ? `: “${review.comment}”` : '.'}
        {review.state === 'rejected' ? ' Reading it again is a new request, from the document’s Extractions.' : ''}
      </Alert>
    )
  }
  return null
}

/** What it was read as, its state and whether it adds up — the screen's heading line. */
export function ExtractionHeading({ record, types, actions }: { record: ExtractionRecord; types: DocumentTypeOption[]; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h2 className="text-foreground text-lg font-semibold">Read as {documentTypeLabel(profileOf(record), types)}</h2>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <ExtractionStateBadge state={record.review?.state} />
          <ReconciledBadge reconciled={record.reconciled} />
          <Badge tone="neutral">{channelLabel(record.channel)}</Badge>
          {record.stale && <Badge tone="warning">An earlier version</Badge>}
          <span className="text-muted-foreground text-sm">{record.created_at ? `read ${formatDateTime(record.created_at)}` : ''}</span>
        </div>
      </div>
      {actions && <div className="flex shrink-0 gap-2">{actions}</div>}
    </div>
  )
}

/* -------------------------------------------------- the component */

export interface ProDocExtractionProps extends HostProps {
  documentId: string
  extractionId: string
  /** The host's way back — its `<Link>` to the document. */
  back?: ReactNode
  /** The host's way to the document's own screen. Not offered without it. */
  onOpenDocument?: (document: ProDocDocument) => void
  /** How a PDF is opened — PDF.js unless a host or a test says otherwise. */
  openPdf?: PdfOpener
  /** How a scanned image is decoded — the browser's own decoder unless a host or a test says otherwise. */
  openImage?: ImageOpener
  className?: string
}

/** One extraction, read: the page, the fields with their evidence, the checks and how it was read. */
export function ProDocExtraction(props: ProDocExtractionProps) {
  const { documentId, extractionId, back, onOpenDocument, openPdf, openImage, className } = props
  const client = useProDocClient(props)
  const loaded = useExtraction(client, documentId, extractionId)
  return (
    <div className={cn('space-y-6', className)}>
      {back}
      <ExtractionBody loaded={loaded} client={client} openPdf={openPdf} openImage={openImage} onOpenDocument={onOpenDocument} />
    </div>
  )
}

/** The loading, refused, waiting and read states every extraction screen shares. */
export function ExtractionBody({
  loaded,
  client,
  openPdf,
  openImage,
  onOpenDocument,
  render,
}: {
  loaded: LoadedExtraction
  client: ProDocClient
  openPdf?: PdfOpener
  openImage?: ImageOpener
  onOpenDocument?: (document: ProDocDocument) => void
  /** The review's own rendering of a readable extraction; the plain view otherwise. */
  render?: (doc: ProDocDocument, extraction: Extraction) => ReactNode
}) {
  const { doc, extraction, waiting, types } = loaded
  if (loaded.loading && !extraction && !waiting) {
    return (
      <SkeletonRegion label="Loading what was read" className="space-y-6">
        <Skeleton className="h-7 w-72" />
        <div className="grid gap-6 lg:grid-cols-5">
          <Skeleton className="h-[60vh] rounded-2xl lg:col-span-3" />
          <Card className="space-y-3 p-6 lg:col-span-2">
            <SkeletonText w="w-40" />
            <SkeletonText />
            <SkeletonText w="w-2/3" />
          </Card>
        </div>
      </SkeletonRegion>
    )
  }
  if (!doc || (!extraction && !waiting)) {
    const missing = /not found|does not exist/i.test(loaded.error) || !loaded.error
    return (
      <EmptyState
        title={missing ? 'Extraction not found' : 'This extraction could not be loaded'}
        description={missing ? 'It may belong to a document outside the workspaces you see.' : loaded.error}
        action={
          missing ? undefined : (
            <Button variant="outline" onClick={() => void loaded.reload()}>
              Try again
            </Button>
          )
        }
      />
    )
  }
  const open = onOpenDocument ? (
    <Button variant="outline" onClick={() => onOpenDocument(doc)}>
      Open the document
    </Button>
  ) : undefined
  if (!extraction && waiting) {
    // FR-E19: its fields wait for a reviewer; the record and the reasons do not.
    return (
      <div className="space-y-6">
        {waiting.record ? <ExtractionHeading record={waiting.record} types={types} actions={open} /> : null}
        <EmptyState
          title="Waiting for review"
          description={
            <>
              What was read from “{doc.title}” is shown once a reviewer accepts it.
              {waiting.reasons.length > 0 ? ` It waits because: ${waiting.reasons.map((r) => reviewReasonLabel(r).toLowerCase()).join('; ')}.` : ''}
            </>
          }
        />
      </div>
    )
  }
  const ready = extraction!
  return (
    <div className="space-y-6">
      <ExtractionHeading record={ready} types={types} actions={open} />
      <p className="text-muted-foreground text-sm">
        From “{doc.title}”.{ready.stale ? ' The document has a newer version than the one read here; reading the current one is a new request.' : ''}
      </p>
      {render ? render(doc, ready) : (
        <>
          <ReviewStateAlert record={ready} />
          <ExtractionView client={client} doc={doc} extraction={ready} types={types} openPdf={openPdf} openImage={openImage} />
        </>
      )}
    </div>
  )
}
